/**
 * Step 1 — Marketing (US-110, BR-110, BR-113). Writes only Unit data: the profile through
 * MarketingEditor (one save for every edited part) and photos through PhotoGrid (saved as they are
 * added). The completeness meter reads saved data only: the profile query and the approved photos.
 */

import { useImperativeHandle, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MarketingEditor } from '../../../../components/unit-marketing/MarketingEditor';
import { PhotoGrid } from '../../../../components/unit-marketing/PhotoGrid';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Skeleton } from '../../../../components/ui/skeleton';
import { useMarketingProfile } from '../../../../hooks/useMarketingProfile';
import { unitPhotosQueryOptions } from '../../../../hooks/useUnitPhotos';
import {
  computeMarketingCompleteness,
  joinPartNames,
  missingMinimumPhrase,
  type MarketingCompleteness,
} from '../../../../lib/marketingCompleteness';
import type { GuideStepHandle, GuideStepProps } from '../guide-steps';

const MAX_PHOTOS = 20;

/** What to say under the meter: what would reach the minimum, or what would take it to 100%. */
function completenessHint(completeness: MarketingCompleteness): string {
  const { minimumMet, missing, percent } = completeness;
  if (!minimumMet) {
    return `Add ${missingMinimumPhrase(missing)} to reach the minimum. You can still continue.`;
  }
  if (percent === 100) return 'Every part is filled in.';
  return `The minimum is a description and one approved photo. Add ${joinPartNames(missing)} to reach 100%.`;
}

function CompletenessMeter({ completeness }: { completeness: MarketingCompleteness }) {
  const { percent, partsDone, partsTotal, minimumMet } = completeness;
  return (
    <div data-testid="marketing-completeness">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-foreground" data-testid="marketing-completeness-label">
          <strong className="font-semibold">{percent}% complete</strong>
          <span className="text-muted-foreground">
            {' '}
            · {partsDone} of {partsTotal} parts
          </span>
        </p>
        <Badge variant={minimumMet ? 'success' : 'warning'} data-testid="marketing-completeness-badge">
          {minimumMet ? 'Minimum met' : 'Needs attention'}
        </Badge>
      </div>
      <div
        role="progressbar"
        aria-label="Marketing completeness"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="mt-2 h-2 overflow-hidden rounded-full bg-muted"
        data-testid="marketing-completeness-bar"
      >
        <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
      </div>
      <p className="mt-2 text-xs text-muted-foreground" data-testid="marketing-completeness-hint">
        {completenessHint(completeness)}
      </p>
    </div>
  );
}

function StepSkeleton() {
  return (
    <div className="space-y-3" data-testid="marketing-step-loading" aria-busy="true">
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-2 w-full" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  );
}

export function MarketingStep({ unitId, handleRef, onDirtyChange, onSavingChange }: GuideStepProps) {
  const editorRef = useRef<GuideStepHandle>(null);
  useImperativeHandle(
    handleRef,
    () => ({
      save: async () => (await editorRef.current?.save()) ?? true,
      isDirty: () => editorRef.current?.isDirty() ?? false,
    }),
    []
  );

  const { profile, isLoading: profileLoading, error: profileError } = useMarketingProfile(unitId);
  const photos = useQuery(unitPhotosQueryOptions(unitId));
  const loading = profileLoading || photos.isLoading;
  const approved = (photos.data ?? []).filter((photo) => photo.status === 'approved').length;
  const processing = (photos.data ?? []).length - approved;

  return (
    <div data-testid="guide-step-marketing">
      <h1 className="text-2xl font-semibold text-foreground" data-testid="guide-step-heading">
        Marketing
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        This is what prospects read first. It is saved on the unit, so every future listing for this unit starts from it.
      </p>

      <div className="mt-4 mb-6">
        {photos.isError ? (
          <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2" data-testid="marketing-completeness-error">
            <p className="text-sm text-destructive">Could not check your photos.</p>
            <Button variant="outline" onClick={() => void photos.refetch()} data-testid="marketing-completeness-retry">
              Retry
            </Button>
          </div>
        ) : loading ? (
          <StepSkeleton />
        ) : profileError ? null : (
          <CompletenessMeter completeness={computeMarketingCompleteness(profile, photos.data ?? [])} />
        )}
      </div>

      <MarketingEditor
        ref={editorRef}
        unitId={unitId}
        hideSaveButton
        onDirtyChange={onDirtyChange}
        onSavingChange={onSavingChange}
        photosSlot={
          <section className="rounded-lg border border-border bg-card p-4 md:p-6" data-testid="marketing-photos-card">
            <h2 className="text-base font-semibold text-foreground">Photos</h2>
            <p className="mb-3 text-xs text-muted-foreground" data-testid="marketing-photos-summary">
              {approved} ready
              {processing > 0 ? ` · ${processing} processing` : ''} · up to {MAX_PHOTOS}. The first photo is the cover.
            </p>
            <PhotoGrid unitId={unitId} />
          </section>
        }
      />
    </div>
  );
}
